'use client';

import { useQuery } from '@tanstack/react-query';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar,
  TrendingUp,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/utils/trpc';

interface RecurringPreviewProps {
  recurringId: string;
  open: boolean;
  onClose: () => void;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  const today = new Date();
  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  let relativeLabel = '';
  if (diffDays === 0) relativeLabel = ' (Hoje)';
  else if (diffDays === 1) relativeLabel = ' (Amanhã)';
  else if (diffDays < 0) relativeLabel = ` (${Math.abs(diffDays)} dias atrás)`;
  else if (diffDays <= 7) relativeLabel = ` (Em ${diffDays} dias)`;

  return {
    formatted: date.toLocaleDateString('pt-BR', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    }),
    relative: relativeLabel,
    isPast: diffDays < 0,
    isToday: diffDays === 0,
    isSoon: diffDays >= 0 && diffDays <= 7,
  };
}

export function RecurringPreview({
  recurringId,
  open,
  onClose,
}: RecurringPreviewProps) {
  // Get the recurring transaction details
  const recurringTransaction = useQuery(
    trpc.recurring.get.queryOptions({ id: recurringId }),
    { enabled: open && !!recurringId }
  );

  // Generate projections for next 6 months
  const projections = useQuery(
    trpc.recurring.generateProjections.queryOptions({
      startDate: new Date().toISOString(),
      endDate: new Date(
        Date.now() + 6 * 30 * 24 * 60 * 60 * 1000
      ).toISOString(), // 6 months from now
      includeInactive: false,
    }),
    { enabled: open }
  );

  if (!recurringTransaction.data) {
    return null;
  }

  const recurring = recurringTransaction.data;

  // Filter projections to only show this specific recurring transaction
  const recurringProjections =
    projections.data?.filter((p) => p.recurringTransactionId === recurringId) ||
    [];

  // Take only first 12 occurrences for display
  const nextOccurrences = recurringProjections.slice(0, 12);

  // Calculate total for the period
  const totalAmount = nextOccurrences.reduce(
    (sum, projection) => sum + Number(projection.amount),
    0
  );

  return (
    <Dialog onOpenChange={onClose} open={open}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {recurring.type === 'credit' ? (
              <ArrowUpCircle className="h-5 w-5 text-green-600" />
            ) : (
              <ArrowDownCircle className="h-5 w-5 text-red-600" />
            )}
            Próximas Ocorrências
          </DialogTitle>
          <DialogDescription>
            <div className="space-y-1">
              <div className="font-medium">{recurring.description}</div>
              <div className="flex items-center gap-2">
                {recurring.category && (
                  <Badge className="text-xs" variant="outline">
                    {recurring.category.icon} {recurring.category.name}
                  </Badge>
                )}
                <Badge
                  className={`text-xs ${
                    recurring.type === 'credit'
                      ? 'bg-green-500/20 text-green-700 dark:text-green-300'
                      : 'bg-red-500/20 text-red-700 dark:text-red-300'
                  }`}
                  variant={
                    recurring.type === 'credit' ? 'default' : 'secondary'
                  }
                >
                  {recurring.type === 'credit' ? 'Receita' : 'Gasto'}
                </Badge>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary Card */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium text-sm">Próximos 6 meses</span>
                </div>
                <div
                  className={`font-bold font-mono ${
                    recurring.type === 'credit'
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {recurring.type === 'credit' ? '+' : '-'}
                  {formatCurrency(totalAmount)}
                </div>
              </div>
              <div className="mt-1 text-muted-foreground text-xs">
                {nextOccurrences.length} ocorrências previstas
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Occurrences */}
          <div className="space-y-2">
            <h4 className="flex items-center gap-2 font-medium text-sm">
              <Calendar className="h-4 w-4" />
              Próximas Ocorrências
            </h4>

            {nextOccurrences.length === 0 ? (
              <div className="py-6 text-center text-muted-foreground">
                <Calendar className="mx-auto mb-2 h-8 w-8 opacity-50" />
                <p className="text-sm">Nenhuma ocorrência programada</p>
              </div>
            ) : (
              <div className="max-h-[300px] space-y-1 overflow-y-auto">
                {nextOccurrences.map((projection, index) => {
                  const dateInfo = formatDate(projection.date);

                  return (
                    <div
                      className={`flex items-center justify-between rounded-lg border p-3 transition-colors ${
                        dateInfo.isToday
                          ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950'
                          : dateInfo.isSoon
                            ? 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950'
                            : 'border-border hover:bg-muted'
                      }`}
                      key={projection.id}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`h-2 w-2 rounded-full ${
                            dateInfo.isToday
                              ? 'bg-blue-500'
                              : dateInfo.isSoon
                                ? 'bg-orange-500'
                                : 'bg-muted-foreground'
                          }`}
                        />
                        <div>
                          <div className="font-medium text-sm">
                            {dateInfo.formatted}
                            {dateInfo.relative && (
                              <span
                                className={`ml-1 text-xs ${
                                  dateInfo.isToday
                                    ? 'text-blue-600'
                                    : dateInfo.isSoon
                                      ? 'text-orange-600'
                                      : 'text-muted-foreground'
                                }`}
                              >
                                {dateInfo.relative}
                              </span>
                            )}
                          </div>
                          <div className="text-muted-foreground text-xs">
                            Ocorrência #{index + 1}
                          </div>
                        </div>
                      </div>
                      <div
                        className={`font-medium font-mono text-sm ${
                          recurring.type === 'credit'
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {recurring.type === 'credit' ? '+' : '-'}
                        {formatCurrency(Number(projection.amount))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {nextOccurrences.length >= 12 && (
            <div className="border-t py-2 text-center text-muted-foreground text-xs">
              Mostrando as próximas 12 ocorrências • Esta transação continuará
              além deste período
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
